import "../../../pages/townson-dashboard-list/townson-dashabord-list.scss";

import React, { useEffect, useRef, useState } from "react";

import PdfImages from "../../../pages/townson-dashboard-list/components/client-data-upload-component/PdfImages/PdfImages";
import UploadPDFsToLocations from "../../../pages/townson-dashboard-list/components/client-data-upload-component/upload-pdfs-to-locations";
import Icon from "../../Icon/Icon";
import Tooltip from "../../tooltip/tooltip";
import ExcelTemplate from "./ExcelTemplate";
import GeneralLocationFields from "./GeneralLocationFields";
import IndustrialFields from "./IndustrialFields";
import OfficeFields from "./OfficeFields";

function AddEditLocations({
	proposedLocationIndex,
	setLocation,
	locationDescription,
	setLocationDescription,
	setProposedLocationIndex,
	handleFocus = () => {},
	handleBlur = () => {},
	maxLengthTextArea,
	maxAllowed,
	handleOnChangeFile,
	csvError,
	location = { currentLocation: {}, proposedLocations: [{}] },
	error,
	errorMessage,
	handleLocationPDFFiles,
	proposedLocations,
	pdfFileName,
	progressFileUpload,
	errorFileUpload,
	removePDFFile,
	cancelPDFUpload,
	screenLocation = "score-insights",
	dashboardData,
	officeLocationType,
	photos,
	setPhotos,
}) {
	const [errorLocationValidation, setErrorLocationValidation] = useState([]);

	// Tracks whether the user has manually reordered locations.
	// When false, AI-suggested pending locations are sorted to the top automatically.
	// When true, the actual array order is used as-is.
	const [isManuallyOrdered, setIsManuallyOrdered] = useState(false);

	// Holds the visual position index of the card we need to scroll into view
	// after the next render. Cleared after scrolling.
	const [pendingScrollIndex, setPendingScrollIndex] = useState(null);

	// One ref slot per visible card, keyed by visual position.
	// The ref sits on the outer card div so getBoundingClientRect is not affected
	// by the buttons div's position:relative / left:-33px offset styling.
	const cardRefs = useRef([]);

	const dashboardId = dashboardData?._id ?? dashboardData?.id ?? null;

	// Reset manual ordering flag whenever a different dashboard is loaded
	useEffect(() => {
		setIsManuallyOrdered(false);
	}, [dashboardId]);

	// After state updates and the DOM re-renders, scroll so the TOP of the moved
	// card is visible inside the modal body. The buttons always live at the card top,
	// so targeting the card top is equivalent to keeping the buttons in view --
	// and avoids any getBoundingClientRect issues caused by the buttons div's
	// position:relative / left styles
	useEffect(() => {
		if (pendingScrollIndex === null) return;

		const card = cardRefs.current[pendingScrollIndex];
		if (!card) {
			setPendingScrollIndex(null);
			return;
		}

		const container = card.closest(".modal-body, .card-content");

		if (container) {
			const cardRect = card.getBoundingClientRect();
			const containerRect = container.getBoundingClientRect();

			if (cardRect.top < containerRect.top + 10) {
				// Card top is above (or flush with) the container top -- scroll up
				container.scrollBy({
					top: cardRect.top - containerRect.top - 10,
					behavior: "smooth",
				});
			} else if (cardRect.top > containerRect.bottom - 160) {
				// Card top is too close to or below the container bottom.
				// Scroll down so the top of the card (and its buttons) is clearly visible.
				container.scrollBy({
					top: cardRect.top - (containerRect.bottom - 160),
					behavior: "smooth",
				});
			}
		} else {
			card.scrollIntoView({ behavior: "smooth", block: "nearest" });
		}

		setPendingScrollIndex(null);
	}, [pendingScrollIndex, locationDescription]);

	useEffect(() => {
		if (screenLocation === "business-insights") {
			if (dashboardData?.proposedLocations?.length) {
				// Sort by the saved order field before mapping so cards always render
				// in the user's chosen sequence. Legacy records without an order value
				// fall back to 0 and are not affected.
				const sortedProposedLocations = [...dashboardData.proposedLocations].sort(
					(a, b) => (a.order ?? 0) - (b.order ?? 0),
				);

				const mappedLocations = sortedProposedLocations.map((location) => ({
					address: { freeformAddress: location.location_name },
					zip_code: location.zip_code || "",
					position: {
						is_current: location.is_current,
						lat: location.latitude,
						lon: location.longitude,
					},
					_id: location._id,
					id: location._id,
					location_id: location?._id,
					pdfs: (location?.pdfs || []).map((pdf) => ({
						...pdf,
						location_id: location._id,
					})),
				}));

				const existingPdfs = sortedProposedLocations
					.map((location, index) => {
						if (location?.pdfs?.length) {
							return {
								location_index: index,
								location_id: location?._id,
								files: location.pdfs.map((pdf) => ({
									location_id: location?._id,
									name: pdf.name,
									url: pdf.url,
								})),
							};
						}
						return null;
					})
					.filter(Boolean);

				const mappedDescriptions = sortedProposedLocations.map((location) => ({
					is_current: location.is_current,
					opex_value: location.opex_value,
					status: location.status,
					notes: location.notes,
					base_rent: location.base_rent,
					building_sf: location.building_sf,
					car_trailer_parking: location.car_trailer_parking,
					clear_height: location.clear_height,
					docs_grade_level_doors: location.docs_grade_level_doors,
					electric_price_sf: location.electric_price_sf,
					leasing_company: location.leasing_company,
					gross_price_sf: location.gross_price_sf,
					office_sf: location.office_sf,
					parking_ratio: location.parking_ratio,
					percent_leased: location.percent_leased,
					available_sf: location.available_sf,
					year_built: location.year_built,
					location_suite: location.location_suite,
					is_ai_suggested: location.is_ai_suggested ?? false,
					is_added_from_ai: location.is_added_from_ai ?? false,
				}));

				setLocation((prevLocation) => ({
					...prevLocation,
					proposedLocations: mappedLocations,
				}));

				setLocationDescription(mappedDescriptions);

				if (existingPdfs.length > 0) {
					handleLocationPDFFiles({
						type: "existing",
						pdfs: existingPdfs,
					});
				}
			} else {
				setLocation((prevLocation) => ({
					...prevLocation,
					proposedLocations: [
						{
							address: { freeformAddress: "" },
							position: { is_current: false, lat: null, lon: null },
						},
					],
				}));

				setLocationDescription([
					{
						is_current: false,
						notes: "",
						status: "",
						gross_price_sf: "",
						opex_value: "",
						leasing_company: "",
					},
				]);
			}
		} else {
			if (dashboardData && dashboardData?.locations) {
				const mappedLocations = dashboardData.locations.map((location) => ({
					address: { freeformAddress: location.address },
					position: {
						is_current: location.is_current,
						lat: location.lat,
						lon: location.lng,
					},
				}));

				const mappedDescriptions = dashboardData.locations.map((loc) => ({
					is_current: loc.is_current,
					...(loc.is_ai_suggested !== undefined && { is_ai_suggested: loc.is_ai_suggested }),
					...(loc.is_added_from_ai !== undefined && { is_added_from_ai: loc.is_added_from_ai }),
				}));

				setLocation((prevLocation) => ({
					...prevLocation,
					proposedLocations: mappedLocations,
				}));

				setLocationDescription(mappedDescriptions);
			}
		}
	}, [dashboardData, screenLocation]);

	const handleOnDiscriptionChange = (name, value, index) => {
		setLocationDescription((prevDescription) => {
			const updatedDescription = [...prevDescription];
			updatedDescription[index] = {
				...updatedDescription[index],
				[name]: value,
			};
			return updatedDescription;
		});
	};

	const handleRemoveLocation = (index) => {
		if (index > -1) {
			setLocationDescription((prevDescription) => {
				const newDescription = [...prevDescription];
				newDescription.splice(index, 1);
				return newDescription;
			});

			setLocation((prevLocation) => {
				if (prevLocation && prevLocation.proposedLocations) {
					const newProposedLocations = [...prevLocation.proposedLocations];
					newProposedLocations.splice(index, 1);
					return {
						...prevLocation,
						proposedLocations: newProposedLocations,
					};
				}
				return prevLocation;
			});

			setProposedLocationIndex((prevIndex) => prevIndex - 1);
		}
	};

	const addNewLocation = () => {
		setLocation((prev) => ({
			...prev,
			proposedLocations: [
				...prev.proposedLocations,
				{
					address: { freeformAddress: "" },
					position: { is_current: false, lat: null, lon: null },
				},
			],
		}));

		setLocationDescription((prev) => [
			...prev,
			{
				is_current: false,
				notes: "",
				status: "",
				gross_price_sf: "",
				opex_value: "",
				leasing_company: "",
			},
		]);
		setProposedLocationIndex(proposedLocationIndex + 1);
	};

	const handleOnAddressChange = (value, name, index = 0) => {
		if (errorLocationValidation.find((obj) => obj.type === "proposedLocations")) {
			setErrorLocationValidation([]);
		}

		setLocation((prevLocation) => {
			if (name === "currentLocation") {
				return { ...prevLocation, [name]: value };
			} else if (name === "proposedLocations") {
				const newProposedLocations = [...prevLocation.proposedLocations];
				newProposedLocations[index] = value;
				return { ...prevLocation, proposedLocations: newProposedLocations };
			}
			return prevLocation;
		});
	};

	// Move a location one step up or down in the list.
	// Sorts any active AI sort into the arrays first, then applies the move.
	// Queues a scroll so the card follows the user's viewport after the DOM updates.
	const moveLocation = (fromIndex, direction) => {
		const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
		const totalLocations = location.proposedLocations.length;

		if (toIndex < 0 || toIndex >= totalLocations) return;

		// If AI sort is still active, materialize it into the actual arrays first
		// so the move operates on what the user sees, not the raw storage order
		const baseLocations = isManuallyOrdered
			? location.proposedLocations
			: getSortedIndices().map((i) => location.proposedLocations[i]);

		const baseDescriptions = isManuallyOrdered
			? locationDescription
			: getSortedIndices().map((i) => locationDescription[i] || {});

		const newLocations = [...baseLocations];
		const newDescriptions = [...baseDescriptions];

		// Swap the two positions
		[newLocations[fromIndex], newLocations[toIndex]] = [newLocations[toIndex], newLocations[fromIndex]];
		[newDescriptions[fromIndex], newDescriptions[toIndex]] = [newDescriptions[toIndex], newDescriptions[fromIndex]];

		setLocation((prev) => ({ ...prev, proposedLocations: newLocations }));
		setLocationDescription(newDescriptions);

		// The user has taken control of ordering -- stop auto-sorting from now on
		setIsManuallyOrdered(true);

		// Tell the scroll effect which visual position to bring into view after render
		setPendingScrollIndex(toIndex);
	};

	// Returns data indices sorted so AI-suggested pending locations appear first.
	// Only used when the user has not manually reordered yet.
	const getSortedIndices = () => {
		return [...Array(location.proposedLocations.length).keys()].sort((a, b) => {
			const aIsAiPending =
				locationDescription[a]?.is_ai_suggested === true && locationDescription[a]?.is_added_from_ai === false;
			const bIsAiPending =
				locationDescription[b]?.is_ai_suggested === true && locationDescription[b]?.is_added_from_ai === false;
			if (aIsAiPending && !bIsAiPending) return -1;
			if (!aIsAiPending && bIsAiPending) return 1;
			return 0;
		});
	};

	// The list of data indices in the order they should appear on screen
	const displayIndices = isManuallyOrdered
		? [...Array(location.proposedLocations.length).keys()]
		: getSortedIndices();

	const totalLocations = location.proposedLocations.length;

	return (
		<>
			<div className='card-title'>
				<label style={{ marginRight: 0 }} htmlFor=''>
					Locations
				</label>
				<span className='sub-text'>You may add up to {maxAllowed} locations</span>
			</div>
			{error && (
				<span className='sub-text' style={{ color: "red" }}>
					*{errorMessage}
				</span>
			)}

			<div className='form-row show-all'>
				{location?.proposedLocations?.length > 0
					? displayIndices.map((dataIndex, visualPosition) => {
							const isNotAdded =
								!isManuallyOrdered &&
								locationDescription[dataIndex]?.is_ai_suggested === true &&
								locationDescription[dataIndex]?.is_added_from_ai === false;

							return (
								<div
									style={{ width: "98%" }}
									className={`d-flex mx-auto group-field${isNotAdded ? " not-added" : ""}`}
									key={dataIndex}
									ref={(el) => (cardRefs.current[visualPosition] = el)}
								>
									{/* Up / down reorder buttons -- hidden when only one location exists */}
									{totalLocations > 1 && (
										<div
											className='position-relative location-reorder-buttons d-flex flex-column'
											style={{
												top: "16px",
												left: "-16px",
												display: "flex",
												gap: "4px",
												marginBottom: "8px",
											}}
										>
											<button
												type='button'
												className='bg-transparent border-0 pointer-cursor cursor-pointer'
												style={{ padding: "2px 8px" }}
												disabled={visualPosition === 0}
												onClick={() => moveLocation(visualPosition, "up")}
												title='Move location up'
											>
												<Icon
													className='mb-0 border-radius-0 bg-light-blue'
													icon='arrow-up-s-fill'
													size='md'
												/>
											</button>
											<button
												type='button'
												className='bg-transparent border-0 pointer-cursor cursor-pointer position-relative'
												style={{ padding: "2px 8px", top: "-10px" }}
												disabled={visualPosition === totalLocations - 1}
												onClick={() => moveLocation(visualPosition, "down")}
												title='Move location down'
											>
												<Icon
													className='border-radius-0 bg-light-blue'
													icon='arrow-down-s-fill'
													size='md'
												/>
											</button>
										</div>
									)}

									<div className='w-100'>
										{totalLocations > 1 && (
											<div
												style={{ width: "50px", height: "50px", top: "-26px" }}
												className='icon-remove d-block z-1'
												onClick={() => handleRemoveLocation(dataIndex)}
											>
												<Icon
													theme='primary'
													className='pointer svg-fill--primary border-0'
													size='lg'
													icon={"close-circle-line"}
												/>
											</div>
										)}

										<GeneralLocationFields
											isNotAdded={isNotAdded}
											handleOnAddressChange={handleOnAddressChange}
											handleFocus={handleFocus}
											handleBlur={handleBlur}
											location={location}
											index={dataIndex}
											handleOnDiscriptionChange={handleOnDiscriptionChange}
											locationDescription={locationDescription}
											maxLengthTextArea={maxLengthTextArea}
										/>

										<div>
											{!officeLocationType ? (
												<OfficeFields
													handleFocus={() => {}}
													handleBlur={() => {}}
													index={dataIndex}
													handleOnDiscriptionChange={handleOnDiscriptionChange}
													locationDescription={locationDescription}
												/>
											) : (
												<IndustrialFields
													handleFocus={() => {}}
													handleBlur={() => {}}
													index={dataIndex}
													handleOnDiscriptionChange={handleOnDiscriptionChange}
													locationDescription={locationDescription}
												/>
											)}
										</div>

										<UploadPDFsToLocations
											index={dataIndex}
											handleLocationPDFFiles={handleLocationPDFFiles}
											location={
												proposedLocations?.proposedLocations
													? proposedLocations?.proposedLocations[dataIndex]
													: {}
											}
											pdfFileName={pdfFileName?.filter(
												(files) => files.location_index === dataIndex,
											)}
											progressFileUpload={progressFileUpload}
											errorFileUpload={errorFileUpload}
											removePDFFile={removePDFFile}
											cancelPDFUpload={cancelPDFUpload}
										/>

										<div>
											{proposedLocations?.proposedLocations &&
												proposedLocations?.proposedLocations[dataIndex] &&
												proposedLocations?.proposedLocations[dataIndex].pdfs &&
												proposedLocations?.proposedLocations[dataIndex].pdfs.some(
													(pdf) => pdf.extracted_images?.length > 0,
												) && (
													<PdfImages
														index={dataIndex}
														locationId={proposedLocations?.proposedLocations[dataIndex].id}
														images={proposedLocations?.proposedLocations[
															dataIndex
														].pdfs.map((pdf) => ({
															location_id: pdf.location_id,
															pdf_name: pdf.name,
															extracted_images: pdf.extracted_images || [],
														}))}
														photos={photos}
														setPhotos={setPhotos}
													/>
												)}
										</div>
									</div>
								</div>
							);
					  })
					: null}

				{location?.proposedLocations?.length < maxAllowed && (
					<div className='text-center add-more-filed-btn'>
						<button
							className='btn primary rounded'
							onClick={(e) => {
								e.preventDefault();
								addNewLocation();
							}}
						>
							Add Another Location
						</button>
						<div className='upload-btn-wrapper'>
							<div className='upload-area'>
								<input
									type='file'
									name='file'
									id='file'
									onChange={handleOnChangeFile}
									onClick={(event) => {
										event.target.value = null;
									}}
								/>
								<b>Upload Location Template (xlsx/csv)</b>
							</div>
							<span className='fm_error'>
								{csvError.map((data, index) => (
									<React.Fragment key={index}>
										{data}
										<br />
									</React.Fragment>
								))}
							</span>
						</div>
					</div>
				)}
				<ExcelTemplate officeLocationType={officeLocationType} />
			</div>
		</>
	);
}

export default AddEditLocations;
